import axios from '@fs/zion-axios'
import { getUser } from '@fs/zion-user'

// The backend calls to Salesforce can sometimes take longer than usual. So we need to increase the timeout for these requests.
// axios.defaults.timeout = 10000 // 10 seconds

export const addNewPortalUser = async (account) => {
  const { cisId } = await getUser()
  const res = await axios.post(
    `/service/public/dev/portal/developers`,
    account?.companyName?.length > 0
      ? {
          cisUserId: cisId,
          email: account?.email,
          name: account?.name,
          title: account?.title,
          account: {
            name: account?.companyName,
            ein: account?.ein,
          },
        }
      : {
          cisUserId: cisId,
          email: account?.email,
          name: account?.name,
          // Don't send the account object for individual users
        },
    {
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 15000, // 15 seconds
    }
  )
  return res
}

export const updatePortalDeveloper = async (accountInfo, formValues) => {
  const { cisId } = await getUser()
  const res = await axios.post(
    `/service/public/dev/portal/developers/${accountInfo?.developerId}`,
    {
      cisUserId: cisId,
      id: accountInfo?.developerId,
      email: formValues?.email,
      name: formValues?.name,
      title: formValues?.title,
      sfContactId: accountInfo?.sfContactId,
      account: {
        name: formValues?.companyName,
        sfAccountId: accountInfo?.account?.sfAccountId,
        ein: accountInfo?.account?.ein,
      },
    },
    {
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 15000, // 15 seconds
    }
  )
  return res
}

export const upgradeAccountToCompany = async (accountInfo, formValues) => {
  const { cisId } = await getUser()
  const res = await axios.post(
    `/service/public/dev/portal/developers/${accountInfo?.developerId}`,
    {
      cisUserId: cisId,
      id: accountInfo?.developerId,
      email: accountInfo?.email,
      name: accountInfo?.name,
      title: formValues?.title,
      sfContactId: accountInfo?.sfContactId,
      account: {
        name: formValues?.companyName,
        sfAccountId: accountInfo?.account?.sfAccountId,
        ein: formValues?.ein,
      },
    },
    {
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 10000, // 10 seconds
    }
  )
  return res
}

export const getPortalDeveloperDetails = async () => {
  const res = await axios.get(`/service/public/dev/portal/developers/users/CURRENT`, {
    validateStatus: (status) => status === 200 || status === 404,
  })
  return res
}

export const getAllApplicationsFromDB = async () => {
  const res = await axios.get(`/service/public/dev/portal/applications/users/CURRENT`)
  return res
}

export const updateApplicationOnDB = async (values, applicationFields) => {
  const {
    betaDeveloperKeyId,
    productionDeveloperKeyId,
    betaRedirectUrls,
    productionRedirectUrls,
    createdDate,
    ...fieldsWithoutKeys
  } = applicationFields // We can't send the developer keys when updating application details, so destructure them out.
  const res = await axios.post(
    `/service/public/dev/portal/applications/${applicationFields?.id}`,
    {
      ...values,
      ...fieldsWithoutKeys,
    },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    }
  )
  return res
}

export const addNewRedirectUrl = async (devKey, env, redirectUrl) => {
  const res = await axios.post(
    `/service/public/dev/portal/devkey/${devKey}/redirects`,
    {},
    {
      headers: {
        'Content-Type': 'application/json',
      },
      params: {
        isBetaUrl: env === 'beta',
        redirectUrl,
      },
    }
  )
  return res
}

export const updateRedirectUrl = async (devKey, env, oldRedirectUrl, newRedirectUrl) => {
  const res = await axios.put(
    `/service/public/dev/portal/devkey/${devKey}/redirects`,
    {},
    {
      headers: {
        'Content-Type': 'application/json',
      },
      params: {
        isBetaUrl: env === 'beta',
        oldRedirectUrl,
        newRedirectUrl,
      },
    }
  )
  return res
}

export const deleteRedirectUrl = async (devKey, env, redirectUrl) => {
  const res = await axios.delete(`/service/public/dev/portal/devkey/${devKey}/redirects`, {
    headers: {
      'Content-Type': 'application/json',
    },
    params: {
      isBetaUrl: env === 'beta',
      redirectUrl,
    },
  })
  return res
}

export const deleteApplicationFromDB = async (id) => {
  const res = await axios.delete(`/service/public/dev/portal/applications/${id}`)
  return res
}

export const postNewApplication = async (application, developerId) => {
  const res = await axios.post(
    `/service/public/dev/portal/applications`,
    {
      name: application?.name,
      type: application?.type,
      expectedVolume: application?.expectedVolume,
      email: application?.email,
      description: application?.description,
      developerId,
    },
    {
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 10000, // 10 seconds
    }
  )
  return res
}

export const getDeveloperByEmail = async (emailAddress) => {
  const encodedEmailAddress = encodeURIComponent(emailAddress)
  const res = await axios.get(`/service/public/dev/portal/admin/developers?emailAddress=${encodedEmailAddress}`)
  return res
}

export const getDeveloperAppsById = async (developerId) => {
  const res = await axios.get(`/service/public/dev/portal/admin/developers/${developerId}/applications`)
  return res
}

export const getApplicationsBySfContactId = async (sfContactId) => {
  const res = await axios.get(`/service/public/dev/portal/admin/migration/salesforce/contacts/${sfContactId}/solutions`)
  return res
}

export const promoteAppToProd = async (applicationId) => {
  // We'll need some error handling here in case the request fails. We'll need some UI changes as well to reflect the error to the user.
  const res = await axios.post(`/service/public/dev/portal/admin/applications/${applicationId}/production-approval`)
  return res
}
